// Audio notification settings and sound management

const SOUND_STORAGE_KEY = 'subify_notification_sound';
const VOLUME_STORAGE_KEY = 'subify_notification_volume';
const ENABLED_STORAGE_KEY = 'subify_sound_enabled';

// Available notification sounds
export const NOTIFICATION_SOUNDS = [
  {
    id: 'default',
    name: 'ExpressSub Chime',
    description: 'Default two-tone notification',
    file: '/sounds/notification.mp3'
  },
  {
    id: 'chime',
    name: 'Triple Chime',
    description: 'Ascending three-note chime',
    file: '/sounds/notification-chime.mp3'
  },
  {
    id: 'urgent',
    name: 'Urgent Alert',
    description: 'Attention-grabbing double beep',
    file: '/sounds/notification-urgent.mp3'
  }
];

// Get current sound setting
export const getCurrentSound = () => {
  const soundId = localStorage.getItem(SOUND_STORAGE_KEY) || 'default';
  return NOTIFICATION_SOUNDS.find(s => s.id === soundId) || NOTIFICATION_SOUNDS[0];
};

// Set notification sound
export const setNotificationSound = (soundId) => {
  localStorage.setItem(SOUND_STORAGE_KEY, soundId);
};

// Get volume (0-100)
export const getVolume = () => {
  const volume = localStorage.getItem(VOLUME_STORAGE_KEY);
  return volume !== null ? parseInt(volume, 10) : 80;
};

// Set volume (0-100)
export const setVolume = (volume) => {
  localStorage.setItem(VOLUME_STORAGE_KEY, Math.min(100, Math.max(0, volume)).toString());
};

// Check if sound is enabled
export const isSoundEnabled = () => {
  const enabled = localStorage.getItem(ENABLED_STORAGE_KEY);
  return enabled !== 'false'; // Default to true
};

// Enable/disable sound
export const setSoundEnabled = (enabled) => {
  localStorage.setItem(ENABLED_STORAGE_KEY, enabled.toString());
};

// =========================================================
// Web Audio API context — survives iOS autoplay restrictions
// once unlocked by a single user gesture (tap/click).
// =========================================================
let audioCtx = null;

function getAudioContext() {
  if (!audioCtx) {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      audioCtx = new AudioContext();
    }
  }
  return audioCtx;
}

// Must be called from a user gesture (click/tap) at least once to unlock iOS audio
export const unlockAudio = () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
  // Also create and play a silent buffer to fully unlock iOS
  if (ctx) {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  }
};

// Fetch and decode an audio file into a buffer (cached)
const audioBufferCache = {};

async function getAudioBuffer(url) {
  if (audioBufferCache[url]) return audioBufferCache[url];
  const ctx = getAudioContext();
  if (!ctx) return null;
  
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    audioBufferCache[url] = audioBuffer;
    return audioBuffer;
  } catch (_e) {
    return null;
  }
}

// Play audio using Web Audio API (works on iOS after unlock)
async function playViaWebAudio(url, volume) {
  const ctx = getAudioContext();
  if (!ctx) return false;
  
  // Resume context if suspended (iOS suspends after inactivity)
  if (ctx.state === 'suspended') {
    try { await ctx.resume(); } catch (_e) { return false; }
  }
  
  const buffer = await getAudioBuffer(url);
  if (!buffer) return false;
  
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  
  const gainNode = ctx.createGain();
  gainNode.gain.value = volume;
  
  source.connect(gainNode);
  gainNode.connect(ctx.destination);
  source.start(0);
  return true;
}

// Play audio using HTML5 Audio (fallback)
async function playViaHtmlAudio(url, volume) {
  try {
    const audio = new Audio(url);
    audio.volume = volume;
    await audio.play();
    return true;
  } catch (_e) {
    return false;
  }
}

// Audio player instance for previewing
let audioPlayer = null;

// Preview a sound (user-initiated, so always works)
export const previewSound = async (soundId) => {
  const sound = NOTIFICATION_SOUNDS.find(s => s.id === soundId);
  if (!sound) return;

  // Unlock audio on user gesture
  unlockAudio();

  if (audioPlayer) {
    audioPlayer.pause();
    audioPlayer.currentTime = 0;
  }

  audioPlayer = new Audio(sound.file);
  audioPlayer.volume = getVolume() / 100;
  
  try {
    await audioPlayer.play();
  } catch (_e) {
    // Fallback to Web Audio API
    await playViaWebAudio(sound.file, getVolume() / 100);
  }
};

// Stop preview
export const stopPreview = () => {
  if (audioPlayer) {
    audioPlayer.pause();
    audioPlayer.currentTime = 0;
  }
};

// Play notification sound (called when new position arrives or reminder)
export const playNotificationSound = async () => {
  if (!isSoundEnabled()) return;

  const sound = getCurrentSound();
  const volume = getVolume() / 100;
  
  // Try Web Audio API first (better iOS/mobile support)
  const webAudioOk = await playViaWebAudio(sound.file, volume);
  if (webAudioOk) return;
  
  // Fallback to HTML5 Audio
  await playViaHtmlAudio(sound.file, volume);
};

// AudioContext.resume() can stay pending forever without a user gesture
// (autoplay policy), so cap the wait and treat a timeout as "blocked".
const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((resolve) => setTimeout(() => resolve(false), ms)),
]);

// How long to wait for an active service worker registration before giving up.
// `navigator.serviceWorker.ready` never rejects, and never settles when no
// registration exists — an unbounded await would swallow the notification (and
// the chime) with it.
const SW_READY_TIMEOUT_MS = 3000;

/**
 * Raise a system notification for a new posting.
 *
 * The `new Notification()` constructor is NOT the delivery mechanism here:
 * MDN documents that it "throws a TypeError when called in nearly all mobile
 * browsers" — Chrome on Android throws, and the old bare `catch` turned that
 * into "sound only, no visible notification" (KAN-132). The service worker's
 * `showNotification()` works on every platform, so it is the primary path; the
 * constructor is only a fallback for browsers with no active service worker.
 *
 * @param {string} title
 * @param {string} body
 * @param {{tag?: string, url?: string}} [options] - `tag` must match the tag
 *   the backend uses for the same notification so the two paths collapse into
 *   one notification; `url` is where a tap routes the user (the service
 *   worker's notificationclick handler reads `notification.data.url`).
 */
export const sendSystemNotification = async (title, body, options = {}) => {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  const { tag, url } = options;

  const notificationOptions = {
    body,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    tag: tag || `expresssub-${Date.now()}`,
    requireInteraction: true,
    silent: false
  };
  // The service worker's notificationclick handler reads notification.data.url
  // to route the user; without it a tap falls back to the site root.
  if (url) notificationOptions.data = { url };

  try {
    // Bound the wait: `navigator.serviceWorker.ready` never rejects and never
    // settles when there is no registration.
    const registration = ('serviceWorker' in navigator)
      ? await withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS)
      : false;

    if (registration) {
      await registration.showNotification(title, notificationOptions);
    } else {
      // No active service worker (desktop without one registered yet). The
      // constructor throws on mobile browsers — that is caught below.
      const notification = new Notification(title, notificationOptions);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  } catch (error) {
    console.warn('[notifications] could not raise the system notification:', error);
  }

  // Always play the in-app sound, whether or not the notification was raised.
  // Not awaited, and explicitly caught: `sendSystemNotification` is called
  // without `await` by its callers, so a rejection here would surface as an
  // unhandled promise rejection.
  playNotificationSound().catch(() => {});
};

// Test if audio can be played
export const canPlayAudio = async () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === 'running') return true;
  
  try {
    const testAudio = new Audio();
    testAudio.volume = 0;
    await testAudio.play();
    testAudio.pause();
    return true;
  } catch (_e) {
    return false;
  }
};

// Get platform-specific audio settings instructions
export const getAudioSettingsInstructions = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  if (isIOS) {
    return {
      platform: 'iOS',
      title: 'Enable ExpressSub Notifications on iPhone/iPad',
      steps: [
        {
          title: 'Allow Browser Notifications',
          detail: 'When prompted, tap "Allow" to enable notifications from Safari/Chrome'
        },
        {
          title: 'Check Focus Mode',
          detail: 'Go to Settings > Focus and make sure ExpressSub notifications aren\'t silenced'
        },
        {
          title: 'Enable Sound in Safari',
          detail: 'In Safari settings, make sure sounds are allowed for this website'
        },
        {
          title: 'Turn Off Silent Mode',
          detail: 'Make sure the physical silent switch on your iPhone is OFF (no orange showing)'
        },
        {
          title: 'Increase Volume',
          detail: 'Use the volume buttons to ensure your media volume is turned up'
        }
      ],
      tip: 'For the most reliable alerts, install ExpressSub as an app by tapping Share > Add to Home Screen'
    };
  } else if (isAndroid) {
    return {
      platform: 'Android',
      title: 'Enable ExpressSub Notifications on Android',
      steps: [
        {
          title: 'Allow Browser Notifications',
          detail: 'When prompted, tap "Allow" to enable notifications from Chrome'
        },
        {
          title: 'Check App Notification Settings',
          detail: 'Go to Settings > Apps > Chrome > Notifications. Turn them on AND set the importance to "High" — otherwise alerts only make a sound and sit in the shade, they never pop up over your other apps.'
        },
        {
          title: 'Disable Do Not Disturb',
          detail: 'Swipe down and make sure DND mode is OFF, or add ExpressSub as an exception'
        },
        {
          title: 'Set Notification Sound',
          detail: 'Use the sound selector below to choose your preferred alert tone'
        },
        {
          title: 'Check Volume',
          detail: 'Press volume button > tap the settings icon > ensure media/notification volume is up'
        }
      ],
      tip: 'Install ExpressSub as an app for better notification reliability: Menu > Add to Home Screen'
    };
  } else {
    return {
      platform: 'Desktop',
      title: 'Enable ExpressSub Notifications on Desktop',
      steps: [
        {
          title: 'Allow Browser Notifications',
          detail: 'Click "Allow" when prompted, or click the lock icon in the address bar to enable'
        },
        {
          title: 'Check System Notification Settings',
          detail: 'Ensure your operating system allows notifications from your browser'
        },
        {
          title: 'Disable Focus/DND Mode',
          detail: 'Turn off Focus Mode (Mac) or Focus Assist (Windows) to receive alerts'
        },
        {
          title: 'Keep Browser Open',
          detail: 'Keep ExpressSub open in a browser tab to receive real-time alerts'
        },
        {
          title: 'Check Volume',
          detail: 'Ensure your system volume is turned up and not muted'
        }
      ],
      tip: 'For best results, install ExpressSub as a desktop app by clicking the install icon in the address bar'
    };
  }
};

// =========================================================
// KAN-156: School-admin "position filled" chime.
// Distinct from the Sub new-position sounds above (softer, warmer).
// =========================================================
export const POSITION_FILLED_SOUND_FILE = '/sounds/position-filled.mp3';

/**
 * Raise a simple confirmation notification (e.g. "Notifications Enabled!").
 *
 * Deliberately separate from `sendSystemNotification`: this one does not play
 * the alert chime, and — critically — it must NEVER throw.
 *
 * `new Notification()` throws a TypeError in nearly all mobile browsers. When
 * that happened inside the "Enable Notifications" click handler it aborted the
 * handler *before* `subscribeToPush()` ran, so the permission was granted but
 * no push subscription was ever created — which is why Android users never
 * received a notification while iOS users did (KAN-132).
 */
export const showConfirmationNotification = async (title, body) => {
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;

  const options = {
    body,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    tag: `expresssub-confirm-${Date.now()}`,
    requireInteraction: true,
    silent: false
  };

  try {
    if ('serviceWorker' in navigator) {
      const registration = await withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS);
      if (registration) {
        await registration.showNotification(title, options);
        return true;
      }
    }

    // Fallback for browsers with no active service worker. The constructor
    // throws on mobile — caught below, and it must never escape this function.
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    return true;
  } catch (error) {
    console.warn('[notifications] could not raise the confirmation notification:', error);
    return false;
  }
};

/**
 * Play the position-filled chime. Resolves to true if audio played, false if
 * the browser blocked it (no prior user interaction) or audio is unavailable.
 * Never throws.
 */
export const playPositionFilledSound = async () => {
  const volume = getVolume() / 100;
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state !== 'running') {
      await withTimeout(ctx.resume().then(() => true).catch(() => false), 400);
    }
    if (ctx && ctx.state === 'running') {
      const ok = await withTimeout(playViaWebAudio(POSITION_FILLED_SOUND_FILE, volume), 1500);
      if (ok) return true;
    }
  } catch (_e) {
    // fall through to HTML5 audio
  }
  return withTimeout(playViaHtmlAudio(POSITION_FILLED_SOUND_FILE, volume), 1500);
};

/** Preview from a click ("Test Sound"): unlocks audio first, so it should always play. */
export const previewPositionFilledSound = async () => {
  unlockAudio();
  return playPositionFilledSound();
};
